
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { writeLog } from '../misc/logs';
import { ActivatePayload, AppSettings, LoginResult, Person, RegisterPayload, ResetPayload, ResponsePayload, UserBalance } from '../model';
import { AuthService } from './auth.service';

@Injectable()
export class LoginService {
  constructor(private authService: AuthService, private http: HttpClient) {}

  login(username: string, password: string): Observable<ResponsePayload<LoginResult>> {
    const url = `${AppSettings.SERVER}/oauth/token/get`;
    const body: any = {
      grant_type: 'password',
      client_id: AppSettings.SERVER_CLIENT_ID,
      client_secret: AppSettings.SERVER_CLIENT_SECRET,
      username: username,
      password: password,
      scope: ''
    };

    return this.http.post<ResponsePayload<LoginResult>>(url, body).pipe(
      tap(res => {
        writeLog(res);

        if (res && res.data && res.data.access_token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          this.setUserToken(res.data);
          this.setUserLoggedIn(res.data.user);
        }
      })
    );
  }

  getUser(): Observable<ResponsePayload<Person>> {
    return this.http.get<ResponsePayload<Person>>(`${AppSettings.SERVER}/user`)
      .pipe(
        tap(res => {
          if (res.data && res.data.id) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            this.setUserLoggedIn(res.data);
          }
        })
      );
  }

  getUserBalance(): Observable<ResponsePayload<UserBalance>> {
    return this.http.get<ResponsePayload<UserBalance>>(`${AppSettings.SERVER}/user/balance`)
      .pipe(
        tap(res => {
          if (res.data) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            this.setUserBalance(res.data);
          }
        })
      );
  }

  register(body: RegisterPayload): Observable<ResponsePayload<ActivatePayload>> {
    return this.http.post<ResponsePayload<ActivatePayload>>(
      `${AppSettings.SERVER}/auth/register`,
      body
    );
  }

  sendActivateCode(body: ActivatePayload): Observable<ResponsePayload<ActivatePayload>> {
    return this.http.post<ResponsePayload<ActivatePayload>>(
      `${AppSettings.SERVER}/auth/activate/send`,
      body
    );
  }

  submitActivateCode(body: ActivatePayload): Observable<ResponsePayload<ActivatePayload>> {
    return this.http.post<ResponsePayload<ActivatePayload>>(
      `${AppSettings.SERVER}/auth/activate/confirm`,
      body
    );
  }

  createResetPassword(phone: string): Observable<ResponsePayload<void>> {
    return this.http.post<ResponsePayload<void>>(
      `${AppSettings.SERVER}/profile/reset/create`,
      {
        mobile: phone
      }
    );
  }

  doResetPassword(body: ResetPayload): Observable<ResponsePayload<void>> {
    return this.http.post<ResponsePayload<void>>(
      `${AppSettings.SERVER}/profile/reset/perform`,
      body
    );
  }

  private handleError(error: Response) {
    return observableThrowError(error.statusText || 'Server Error');
  }

  storeUserProfile(profile: Person): void {
    localStorage.setItem('login_user', JSON.stringify(profile));
  }

  storeUserAuth(auth: string): void {
    // localStorage.setItem('login_auth', auth);
  }

  getUserProfile(): Person {
    return localStorage.getItem('login_user')
      ? JSON.parse(localStorage.getItem('login_user'))
      : null;
  }

  isAdmin(): boolean {
    return this.getUserProfile().admin;
  }

  isCustomer(): boolean {
    return !this.getUserProfile().admin;
  }

  isActive(): boolean {
    return this.getUserProfile().active;
  }

  getUserIdentity(): string {
    return localStorage.getItem('login_identity');
  }

  setUserIdentity(identity: string): void {
    localStorage.setItem('login_identity', identity);
  }

  setUserToken(token: LoginResult): void {
    localStorage.setItem('token_type', token.token_type);
    localStorage.setItem('access_token', token.access_token);
    localStorage.setItem('expires_in', token.expires_in);
    localStorage.setItem('refresh_token', token.refresh_token);
  }

  setUserLoggedIn(profile: Person): void {
    localStorage.setItem('login_status', 'true');
    this.storeUserProfile(profile);
  }

  setUserBalance(balances: UserBalance): void {
    const profile = this.getUserProfile();
    profile.credits = balances.sms_credits;
    profile.credits_promo = balances.sms_credits_promo;

    this.storeUserProfile(profile);
  }

  setUserLoggedOut(): void {
    localStorage.removeItem('login_status');
    localStorage.removeItem('login_user');
    localStorage.removeItem('token_type');
    localStorage.removeItem('expires_in');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(AppSettings.LOCAL_MESSAGE_STORAGE_KEY);
  }

  isUserLoggedIn(): boolean {
    if (
      localStorage.getItem('login_status') === 'true' &&
      localStorage.getItem('login_user') &&
      // && ((moment.now() - (+localStorage.getItem('login_time'))) < 900000) - use if needed
      localStorage.getItem('access_token')
    ) {
      return true;
    } else {
      this.setUserLoggedOut();
      return false;
    }
  }

  getUserSession(): string {
    return localStorage.getItem('access_token');
  }
}
